/**
 * Hook to integrate realtime WebSocket events with React Query cache invalidation
 * 
 * This hook listens to Socket.io events and invalidates the corresponding React Query cache entries.
 * 
 * Usage:
 * ```tsx
 * // In your app root or layout component
 * useRealtimeInvalidation(company);
 * ```
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { onInvalidate, ensureLiveConnected } from '../lib/live';
import { useClient } from '../contexts/ClientContext';
import {
  clientKeys,
  leadKeys,
  taskKeys,
  orderKeys,
  notificationKeys,
  appointmentKeys,
  calendarKeys,
  authKeys,
  companyKeys,
  agentKeys,
  inventoryKeys,
  contractKeys,
  potentialClientKeys,
} from '../lib/queryKeys';

type InvalidatePayload = {
  type?: string;
  company?: string;
  entityId?: string;
  [key: string]: any;
};

/**
 * Hook to handle realtime cache invalidation
 * 
 * Maps Socket.io events to React Query query keys and invalidates them
 * 
 * @param company - Company slug (optional, will be extracted from payload, client context, or localStorage if not provided)
 */
export function useRealtimeInvalidation(company?: string) {
  const queryClient = useQueryClient();
  const { client } = useClient();

  useEffect(() => {
    ensureLiveConnected();

    // Track refetch state to prevent loops (legacy - used by clients/tasks handlers)
    const refetching = new Set<string>();
    
    // Per-company refetch burst lock with TTL (500ms) - legacy for clients/tasks
    // Key: `leads:${company}`, Value: timestamp when lock was set
    // For leads/tasks: Value can be { inFlight: boolean; pending: boolean; lastEventAt: number }
    const burstLocks = new Map<string, number | { inFlight: boolean; pending: boolean; lastEventAt: number }>();
    const BURST_LOCK_TTL_MS = 500;
    
    // In-flight refetch guard: Track active refetches to prevent duplicate refetch bursts (NEW for leads)
    // Key: `${type}:${company}:${scope || 'all'}:${managerId || ''}`
    // Value: Promise of the refetch operation
    const inFlightRefetches = new Map<string, Promise<any>>();
    
    // Helper to generate in-flight key for refetch guard
    const getInFlightKey = (type: string, company: string, payload: any): string => {
      const scope = payload?.scope || 'all';
      const managerId = payload?.managerId || '';
      // CRITICAL: Include leadId and nonce to prevent false dedup of different mutations
      // This ensures each unique mutation (different leadId or different nonce) gets processed
      const leadId = payload?.id || payload?.leadId || '';
      const nonce = payload?.nonce || '';
      // Build key: type:company:scope:managerId:leadId:nonce
      // If leadId exists, include it; if nonce exists, include it
      const parts = [type, company, scope, managerId];
      if (leadId) parts.push(leadId);
      if (nonce) parts.push(nonce);
      return parts.join(':');
    };

    // Helper to get company from multiple sources
    const getCompany = (payload: InvalidatePayload): string | undefined => {
      // Priority 1: Payload company (most reliable - comes from backend)
      if (payload?.company) return payload.company;
      
      // Priority 2: Hook parameter
      if (company) return company;
      
      // Priority 3: Client context
      if (client?.company) return client.company;
      
      // Priority 4: localStorage
      try {
        const clientData = JSON.parse(localStorage.getItem('client') || '{}');
        if (clientData?.company) return clientData.company;
      } catch {
        // Ignore parse errors
      }
      
      // Priority 5: Extract from window.location.pathname if on a company route
      // This is a fallback for cases where company isn't in payload/context
      if (typeof window !== 'undefined') {
        const pathMatch = window.location.pathname.match(/^\/([^/]+)/);
        if (pathMatch && pathMatch[1] && pathMatch[1] !== 'superadmin') {
          return pathMatch[1];
        }
      }
      
      return undefined;
    };

    const off = onInvalidate((payload: InvalidatePayload) => {
      const eventType = payload?.type;
      const eventCompany = getCompany(payload);

      if (!eventCompany) {
        console.warn('[useRealtimeInvalidation] No company found in payload, hook param, client context, or localStorage', {
          payload,
          hookCompany: company,
          clientCompany: client?.company,
        });
        return;
      }

      // Map event types to query key invalidations
      switch (eventType) {
        case 'clients':
        case 'clients:changed': {
          // CRITICAL: Update cache directly when we have client data (immediate update, no refetch delay)
          // This matches the leads handler pattern for instant UI updates
          if (payload?.op === 'update' && payload?.id && payload?.data) {
            const updatedClient = payload.data;
            const clientId = String(payload.id);
            
            // Update ALL list queries with the new client data (immediate cache update, no refetch)
            queryClient.setQueriesData({ queryKey: clientKeys.all(eventCompany) }, (old: any) => {
              if (!old) return old;
              
              const updateInArray = (arr: any[]) => {
                const index = arr.findIndex((item: any) => String(item?._id || item?.id) === clientId);
                if (index === -1) return arr; // Client not in this list
                return arr.map((item: any, idx: number) => 
                  idx === index ? updatedClient : item
                );
              };

              if (Array.isArray(old)) {
                return updateInArray(old);
              }
              if (old.data && Array.isArray(old.data)) {
                return { ...old, data: updateInArray(old.data) };
              }
              return old;
            });
            
            // Update detail cache
            queryClient.setQueryData(clientKeys.detail(eventCompany, clientId), updatedClient);
            
            // Also invalidate potential clients queries for count updates
            queryClient.invalidateQueries({ queryKey: potentialClientKeys.all(eventCompany) });
            
            // No refetch needed - cache is updated directly for instant UI update
            break;
          }
          
          // CREATE: Add new client to cache (no refetch)
          if (payload?.op === 'insert' || payload?.op === 'create') {
            if (payload?.data) {
              const newClient = payload.data;
              
              queryClient.setQueriesData({ queryKey: clientKeys.all(eventCompany) }, (old: any) => {
                if (!old) return old;
                
                const addToArray = (arr: any[]) => {
                  if (arr.some((item: any) => String(item?._id || item?.id) === String(newClient._id || newClient.id))) {
                    return arr; // Already in list
                  }
                  return [newClient, ...arr];
                };

                if (Array.isArray(old)) {
                  return addToArray(old);
                }
                if (old.data && Array.isArray(old.data)) {
                  return { ...old, data: addToArray(old.data) };
                }
                return old;
              });
              
              // Also invalidate potential clients queries for count updates
              queryClient.invalidateQueries({ queryKey: potentialClientKeys.all(eventCompany) });
            }
            
            // No refetch needed - cache is updated directly
            break;
          }
          
          // For other operations (delete, etc.) or if no data in payload, fall back to refetch
          // This is a fallback for cases where we don't have the full client data
          const allQueryKey = clientKeys.all(eventCompany);
          queryClient.invalidateQueries({ queryKey: allQueryKey });
          
          // Also invalidate potential clients queries
          const allPotentialClientsQueryKey = potentialClientKeys.all(eventCompany);
          queryClient.invalidateQueries({ queryKey: allPotentialClientsQueryKey });
          
          break;
        }

        case 'manager-clients':
        case 'manager-clients:changed': {
          // CRITICAL: Handle manager-specific clients list changes
          // When a client is added/removed from a manager, invalidate that manager's clients query
          // The version token strategy ensures the query will refetch with the latest data
          const managerId = payload?.managerId ? String(payload.managerId) : undefined;
          const action = payload?.action || payload?.op || 'update';
          
          if (managerId && eventCompany) {
            // Invalidate the manager-specific clients query
            // This uses the version token strategy - the query will refetch automatically
            queryClient.invalidateQueries({
              queryKey: clientKeys.managerList(eventCompany, managerId),
              exact: false, // Invalidate all variations (with/without params)
            });
            
            if (process.env.NODE_ENV === 'development') {
              console.log('[useRealtimeInvalidation] Invalidated manager clients query:', {
                company: eventCompany,
                managerId,
                action,
                clientId: payload?.clientId,
                version: payload?.version,
              });
            }
          }
          break;
        }

        case 'leads':
        case 'leads:changed': {
          // ENHANCED LEADS HANDLER - Handles both basic updates and owner changes
          // CLIENTS PIPELINE REFERENCE (EXACT PATTERN):
          // - Backend emits: ONE 'invalidate' event (type='clients', op='update', id, data, company)
          // - Frontend: invalidateQueries for clientKeys.all(company), then refetchQueries with predicate
          // - Predicate matches: ['clients', company, ...], ['managerClients', 'list', company, managerId, ...]
          // - Result: Both admin /clients and manager /clients/my update instantly
          //
          // LEADS PIPELINE (MIRROR CLIENTS EXACTLY):
          // - Backend emits: ONE 'invalidate' event (type='leads', op='update'|'owner-change'|'removed', id, data, company, ownerFrom, ownerTo, nonce)
          // - Frontend: invalidateQueries for leadKeys.all(company), then refetchQueries with predicate
          // - Predicate matches: ['leads', company, 'list', params] where params may include managerId
          // - Result: Both admin /leads and manager /leads/my update instantly
          
          // Extract owner change information from payload (NEW backend format)
          const isOwnerChange = payload?.op === 'owner-change' || payload?.ownerChange === true;
          const ownerFrom = Array.isArray(payload?.ownerFrom) ? payload.ownerFrom.map(String) : 
                           payload?.ownerFrom ? [String(payload.ownerFrom)] : [];
          const ownerTo = Array.isArray(payload?.ownerTo) ? payload.ownerTo.map(String) : 
                         payload?.ownerTo ? [String(payload.ownerTo)] : [];
          const isRemoved = payload?.op === 'removed' || payload?.removed === true;
          const isManagerScope = payload?.scope === 'my' || payload?.managerId;
          const targetManagerId = payload?.managerId ? String(payload.managerId) : undefined;
          
          // Get all active queries for debugging and manager query detection
          // const allActiveQueries = queryClient.getQueryCache().getAll(); // Unused - commented out to fix lint warning
          
          // Use in-flight refetch guard with proper dedup key (includes nonce for owner changes)
          const dedupKey = isOwnerChange && payload?.id && payload?.nonce
            ? `leads:${eventCompany}:${payload.id}:${payload.nonce}`
            : getInFlightKey('leads', eventCompany, payload);
          
          const existingRefetch = inFlightRefetches.get(dedupKey);
          
          if (existingRefetch) {
            // Refetch already in progress - skip starting a new one
            return; // Skip refetch, but event was still processed
          }
          
          // CRITICAL: Do NOT invalidate queries - this causes refetch and lag
          // Instead, update cache directly (like Clients.tsx)
          // The cache updates below are sufficient for immediate UI updates
          // Queries will refetch naturally on next mount/focus due to staleTime: 0
          
          // PHASE 1: Update cache directly with new lead data (same as Clients.tsx)
          // CRITICAL: Update cache BEFORE any refetch to prevent data loss
          // This ensures both admin /leads and manager /leads/my update instantly
          // UPDATE: Update cache directly with new lead data (no refetch, no flicker)
          // CRITICAL FIX: Handle 'update', 'owner-change', and 'removed' operations
          // Only use payload.data if it looks like a lead document (_id). Avoid overwriting cache with
          // non-lead payloads (e.g. attachment delete returns { message, storageState }).
          if ((payload?.op === 'update' || payload?.op === 'owner-change' || payload?.op === 'removed') && payload?.id && payload?.data && payload?.data?._id) {
            const updatedLead = payload.data;
            const isRemoval = isOwnerChange && ownerTo.length === 0 && ownerFrom.length > 0;
            const hasNoManagers = !updatedLead.managerIds || (Array.isArray(updatedLead.managerIds) && updatedLead.managerIds.length === 0);
            
            // Extract manager IDs from updated lead
            const updatedLeadManagerIds = Array.isArray(updatedLead.managerIds)
              ? updatedLead.managerIds.map((m: any) => String(m?._id || m || '')).filter(Boolean)
              : [];
            
            // CRITICAL: First, explicitly handle new query key formats (leadKeys.my and leadKeys.managerList)
            // Handle leadKeys.my() format: ['leads', 'my', company, managerId]
            // Handle leadKeys.managerList() format: ['managerLeads', 'list', company, managerId, params]
            if (isOwnerChange && ownerFrom.length > 0) {
              // Lead was removed from managers - remove from their caches
              for (const removedManagerId of ownerFrom) {
                const managerIdStr = String(removedManagerId);
                const isInUpdatedLead = updatedLeadManagerIds.includes(managerIdStr);
                
                // Only remove if lead is no longer assigned to this manager
                if (!isInUpdatedLead) {
                  const myQueryKey = leadKeys.my(eventCompany, managerIdStr);
                  const managerListQueryKey = leadKeys.managerList(eventCompany, managerIdStr, {});
                  
                  // Remove from both query key formats
                  queryClient.setQueryData(myQueryKey, (old: any) => {
                    if (!old) return old;
                    const removeFromArray = (arr: any[]) => arr.filter((item: any) => String(item?._id) !== String(payload.id));
                    if (Array.isArray(old)) return removeFromArray(old);
                    if (old.data && Array.isArray(old.data)) {
                      return { ...old, data: removeFromArray(old.data), pagination: old.pagination ? { ...old.pagination, total: Math.max(0, (old.pagination.total || 0) - 1) } : old.pagination };
                    }
                    return old;
                  });
                  
                  queryClient.setQueryData(managerListQueryKey, (old: any) => {
                    if (!old) return old;
                    const removeFromArray = (arr: any[]) => arr.filter((item: any) => String(item?._id) !== String(payload.id));
                    if (Array.isArray(old)) return removeFromArray(old);
                    if (old.data && Array.isArray(old.data)) {
                      return { ...old, data: removeFromArray(old.data), pagination: old.pagination ? { ...old.pagination, total: Math.max(0, (old.pagination.total || 0) - 1) } : old.pagination };
                    }
                    return old;
                  });
                }
              }
            }
            
            // CRITICAL FIX: Handle both owner-change and update operations when adding leads to managers
            // When assigning a lead to a manager for the first time, backend emits op: 'update' with ownerTo populated
            // When reassigning, backend emits op: 'owner-change' with ownerTo populated
            if ((isOwnerChange || (payload?.op === 'update' && ownerTo.length > 0)) && ownerTo.length > 0) {
              // Lead was added to managers - add to their caches
              for (const addedManagerId of ownerTo) {
                const managerIdStr = String(addedManagerId);
                const isInUpdatedLead = updatedLeadManagerIds.includes(managerIdStr);
                
                // Only add if lead is now assigned to this manager
                if (isInUpdatedLead) {
                  const myQueryKey = leadKeys.my(eventCompany, managerIdStr);
                  const managerListQueryKey = leadKeys.managerList(eventCompany, managerIdStr, {});
                  
                  // Add to both query key formats
                  queryClient.setQueryData(myQueryKey, (old: any) => {
                    const addToArray = (arr: any[]) => {
                      if (arr.some((item: any) => String(item?._id) === String(payload.id))) return arr;
                      return [updatedLead, ...arr];
                    };
                    if (!old) return { data: [updatedLead] };
                    if (Array.isArray(old)) return addToArray(old);
                    if (old.data && Array.isArray(old.data)) {
                      return { ...old, data: addToArray(old.data), pagination: old.pagination ? { ...old.pagination, total: (old.pagination.total || 0) + 1 } : old.pagination };
                    }
                    return old;
                  });
                  
                  queryClient.setQueryData(managerListQueryKey, (old: any) => {
                    const addToArray = (arr: any[]) => {
                      if (arr.some((item: any) => String(item?._id) === String(payload.id))) return arr;
                      return [updatedLead, ...arr];
                    };
                    if (!old) return { data: [updatedLead] };
                    if (Array.isArray(old)) return addToArray(old);
                    if (old.data && Array.isArray(old.data)) {
                      return { ...old, data: addToArray(old.data), pagination: old.pagination ? { ...old.pagination, total: (old.pagination.total || 0) + 1 } : old.pagination };
                    }
                    return old;
                  });
                }
              }
            }
            
            // Update ALL list queries with the new lead data (silent cache update)
            // This includes both admin queries and manager queries (/leads/my) - old format
            queryClient.setQueriesData({ queryKey: leadKeys.all(eventCompany) }, (old: any, query?: any) => {
              if (!old) return old;
              
              // PHASE 3: NEWER-WINS - Block stale overwrites from websocket events
              // Check if incoming data is newer than cached data
              const incomingUpdatedAt = updatedLead.updatedAt ? new Date(updatedLead.updatedAt).getTime() : 0;
              
              // CRITICAL: Check if this query is filtered by a specific manager
              const queryKey = query?.queryKey;
              let shouldRemoveFromList = false;
              let shouldAddToList = false;
              let managerIdFromParams: string | null = null;
              
              // Skip new query key formats (already handled above)
              if (Array.isArray(queryKey)) {
                // Skip leadKeys.my() format: ['leads', 'my', company, managerId]
                if (queryKey[0] === 'leads' && queryKey[1] === 'my' && queryKey.length >= 4) {
                  return old; // Already handled above
                }
                // Skip leadKeys.managerList() format: ['managerLeads', 'list', company, managerId, params]
                if (queryKey[0] === 'managerLeads' && queryKey[1] === 'list' && queryKey.length >= 4) {
                  return old; // Already handled above
                }
              }
              
              if (Array.isArray(queryKey) && queryKey.length >= 4) {
                try {
                  const filtersKey = queryKey[3];
                  let params: any = null;
                  
                  if (typeof filtersKey === 'string') {
                    params = JSON.parse(filtersKey);
                  } else if (typeof filtersKey === 'object' && filtersKey !== null) {
                    params = filtersKey;
                  }
                  
                  if (params) {
                    const scope = params.scope;
                    managerIdFromParams = params.managerId || (scope && scope !== 'ALL' ? scope : null);
                    
                    // For manager-specific queries (scope === managerId)
                    if (managerIdFromParams && managerIdFromParams !== 'ALL') {
                      const managerIdStr = String(managerIdFromParams);
                      const isInUpdatedLead = updatedLeadManagerIds.includes(managerIdStr);
                      const wasInOldLead = ownerFrom.length > 0 && ownerFrom.includes(managerIdStr);
                      const isInNewLead = ownerTo.length > 0 && ownerTo.includes(managerIdStr);
                      
                      // If this is an owner change (manager assignment/removal)
                      if (isOwnerChange) {
                        // If lead was removed from this manager, remove it from the list
                        if (wasInOldLead && !isInUpdatedLead) {
                          shouldRemoveFromList = true;
                        }
                        // If lead was added to this manager, add it to the list
                        else if (isInNewLead && isInUpdatedLead && !wasInOldLead) {
                          shouldAddToList = true;
                        }
                        // If lead is not assigned to this manager after update, remove it
                        else if (!isInUpdatedLead) {
                          shouldRemoveFromList = true;
                        }
                      } else {
                        // CRITICAL FIX: For regular updates (like adding notes), don't remove leads
                        // even if managerIds are missing from payload. Only update in place.
                        // Only remove if we have explicit evidence the lead was removed from manager
                        // For note updates, managerIds might be missing from payload, so we keep the lead
                        if (isInUpdatedLead) {
                          // Lead is assigned to this manager - keep it and update in place (don't remove)
                          shouldRemoveFromList = false;
                        } else if (updatedLeadManagerIds.length === 0 && ownerFrom.length === 0) {
                          // Only remove if lead explicitly has no managers AND wasn't in old lead
                          // This handles the case where a lead was actually unassigned from all managers
                          // But only if we're certain (no ownerFrom means it wasn't just moved)
                          shouldRemoveFromList = true;
                        } else {
                          // If managerIds are missing from payload but lead might still be assigned,
                          // don't remove it - let the updateInArray function handle it (it will update in place if exists)
                          shouldRemoveFromList = false;
                        }
                      }
                    }
                  }
                } catch {
                  // Ignore parse errors
                }
              }
              
              const updateInArray = (arr: any[]) => {
                const index = arr.findIndex((item: any) => String(item?._id) === String(payload.id));
                
                // CRITICAL: If removing manager and this is a manager-specific filter, remove the lead
                if (shouldRemoveFromList) {
                  if (index === -1) return arr; // Already not in list
                  const filtered = arr.filter((item: any) => 
                    String(item?._id) !== String(payload.id)
                  );
                  return filtered;
                }
                
                // If adding to a manager-specific list and lead doesn't exist, add it
                if (shouldAddToList && index === -1) {
                  // Check if lead should be in this manager's list
                  if (managerIdFromParams && updatedLeadManagerIds.includes(String(managerIdFromParams))) {
                    return [updatedLead, ...arr];
                  }
                  return arr;
                }
                
                // If lead exists in list, update it in place
                // CRITICAL: Preserve existing managerIds if missing from updatedLead (for note updates)
                if (index !== -1) {
                  const existingLead = arr[index];
                  // If updatedLead is missing managerIds but existing lead has them, preserve them
                  const mergedLead = (!updatedLead.managerIds || updatedLead.managerIds.length === 0) && 
                                     existingLead?.managerIds && existingLead.managerIds.length > 0
                    ? { ...updatedLead, managerIds: existingLead.managerIds }
                    : updatedLead;
                  
                  return arr.map((item: any, idx: number) => 
                    idx === index ? mergedLead : item
                  );
                }
                
                // If lead not in this list and we're not adding/removing it, return unchanged
                // For admin queries (scope === 'ALL'), we might want to add it, but that's handled elsewhere
                return arr;
              };
              
              if (Array.isArray(old)) {
                const updated = updateInArray(old);
                return updated;
              }
              if (old.data && Array.isArray(old.data)) {
                const updated = updateInArray(old.data);
                // Update pagination total if lead was removed or added
                let pagination = old.pagination;
                if (shouldRemoveFromList && pagination) {
                  pagination = { ...pagination, total: Math.max(0, (pagination.total || 0) - 1) };
                } else if (shouldAddToList && pagination) {
                  pagination = { ...pagination, total: (pagination.total || 0) + 1 };
                }
                return { ...old, data: updated, pagination };
              }
              if (old.items && Array.isArray(old.items)) {
                const updated = updateInArray(old.items);
                // Update pagination total if lead was removed or added
                let pagination = old.pagination;
                if (shouldRemoveFromList && pagination) {
                  pagination = { ...pagination, total: Math.max(0, (pagination.total || 0) - 1) };
                } else if (shouldAddToList && pagination) {
                  pagination = { ...pagination, total: (pagination.total || 0) + 1 };
                }
                return { ...old, items: updated, pagination };
              }
              if (old.leads && Array.isArray(old.leads)) {
                const updated = updateInArray(old.leads);
                return { ...old, leads: updated };
              }
              return old;
            });
            
            // Update detail cache with NEWER-WINS protection
            // CRITICAL: Preserve managerIds if missing from updatedLead (for note updates)
            queryClient.setQueryData(leadKeys.detail(eventCompany, payload.id), (old: any) => {
              if (!old) {
                // TEMP LOG: PHASE 1 - Track cache write from socket
                const managerIds = Array.isArray(updatedLead.managerIds) ? updatedLead.managerIds.map((m: any) => String(m?._id || m || '')).filter(Boolean) : [];
                console.log('[LEAD-MGR-CACHE]', JSON.stringify(leadKeys.detail(eventCompany, payload.id)), payload.id, JSON.stringify(managerIds), 'socket-event', new Date().toISOString());
                return updatedLead;
              }
              // PHASE 3: NEWER-WINS - Block stale overwrites from websocket
              const oldUpdatedAt = old.updatedAt ? new Date(old.updatedAt).getTime() : 0;
              const incomingUpdatedAt = updatedLead.updatedAt ? new Date(updatedLead.updatedAt).getTime() : 0;
              if (oldUpdatedAt > incomingUpdatedAt) {
                // TEMP LOG: PHASE 1 - Track blocked stale overwrite
                const managerIds = Array.isArray(old.managerIds) ? old.managerIds.map((m: any) => String(m?._id || m || '')).filter(Boolean) : [];
                console.log('[LEAD-MGR-CACHE] BLOCKED STALE', JSON.stringify(leadKeys.detail(eventCompany, payload.id)), payload.id, 
                  `oldUpdatedAt=${old.updatedAt} > incomingUpdatedAt=${updatedLead.updatedAt || 'none'}`, 'socket-event', new Date().toISOString());
                return old; // Keep newer data
              }
              
              // CRITICAL FIX: Preserve managerIds from old lead if missing in updatedLead
              // This prevents leads from losing manager assignments when only notes are updated
              const mergedLead = (!updatedLead.managerIds || updatedLead.managerIds.length === 0) && 
                                 old?.managerIds && old.managerIds.length > 0
                ? { ...updatedLead, managerIds: old.managerIds }
                : updatedLead;
              
              // TEMP LOG: PHASE 1 - Track cache write from socket
              const managerIds = Array.isArray(mergedLead.managerIds) ? mergedLead.managerIds.map((m: any) => String(m?._id || m || '')).filter(Boolean) : [];
              console.log('[LEAD-MGR-CACHE]', JSON.stringify(leadKeys.detail(eventCompany, payload.id)), payload.id, JSON.stringify(managerIds), 'socket-event', new Date().toISOString());
              return mergedLead;
            });
            
            // Debug logs removed
          }
          // CREATE: Add new lead to cache (no refetch)
          else if (payload?.op === 'create' || payload?.op === 'insert') {
            if (!payload?.data) {
              // No data in payload - fall back to invalidation
              queryClient.invalidateQueries({ queryKey: leadKeys.all(eventCompany) });
              break;
            }
            
            const newLead = payload.data;
            
            // Extract manager IDs from new lead
            const newLeadManagerIds = Array.isArray(newLead.managerIds)
              ? newLead.managerIds.map((m: any) => String(m?._id || m || '')).filter(Boolean)
              : [];
            
            // CRITICAL: Add new lead to ALL admin list queries (scope === 'ALL' or no scope)
            // This ensures the lead appears in the admin table immediately
            queryClient.setQueriesData({ 
              predicate: (query) => {
                const key = query.queryKey;
                // Match all lead list queries: ['leads', company, 'list', filtersKey]
                if (!Array.isArray(key) || key.length < 4) return false;
                if (key[0] !== 'leads' || key[1] !== eventCompany || key[2] !== 'list') return false;
                
                // Parse filters to check scope
                try {
                  const filtersKey = key[3];
                  let params: any = null;
                  
                  if (typeof filtersKey === 'string') {
                    params = JSON.parse(filtersKey);
                  } else if (typeof filtersKey === 'object' && filtersKey !== null) {
                    params = filtersKey;
                  }
                  
                  if (params) {
                    const scope = params.scope;
                    // For admin queries (scope === 'ALL' or undefined), always add
                    if (!scope || scope === 'ALL') {
                      return true;
                    }
                    // For manager-specific queries, only add if lead is assigned to this manager
                    const managerIdFromParams = String(scope);
                    return newLeadManagerIds.includes(managerIdFromParams);
                  }
                  // If no params, treat as admin query
                  return true;
                } catch {
                  // If parsing fails, treat as admin query (default to adding)
                  return true;
                }
              }
            }, (old: any) => {
              if (!old) return old;
              
              // Check if lead already exists (prevent duplicates)
              const leads = Array.isArray(old) ? old : (old.data || old.leads || old.items || []);
              const exists = leads.some((l: any) => String(l?._id) === String(newLead._id));
              if (exists) return old;
              
              // Add to beginning of list
              if (Array.isArray(old)) {
                return [newLead, ...old];
              }
              if (old.data && Array.isArray(old.data)) {
                return { 
                  ...old, 
                  data: [newLead, ...old.data],
                  pagination: old.pagination ? { 
                    ...old.pagination, 
                    total: (old.pagination.total || 0) + 1 
                  } : old.pagination
                };
              }
              if (old.items && Array.isArray(old.items)) {
                return { 
                  ...old, 
                  items: [newLead, ...old.items],
                  pagination: old.pagination ? { 
                    ...old.pagination, 
                    total: (old.pagination.total || 0) + 1 
                  } : old.pagination
                };
              }
              if (old.leads && Array.isArray(old.leads)) {
                return { ...old, leads: [newLead, ...old.leads] };
              }
              return old;
            });
            
            // CRITICAL: Also add to managerLeads queries if assigned to managers
            if (newLeadManagerIds.length > 0) {
              newLeadManagerIds.forEach((managerId: string) => {
                const managerQueryKey = leadKeys.managerList(eventCompany, managerId, {});
                queryClient.setQueryData(managerQueryKey, (old: any) => {
                  if (!old) {
                    return { data: [newLead] };
                  }
                  if (old.data && Array.isArray(old.data)) {
                    const exists = old.data.some((l: any) => String(l?._id) === String(newLead._id));
                    if (!exists) {
                      return { 
                        ...old, 
                        data: [newLead, ...old.data],
                        pagination: old.pagination ? { 
                          ...old.pagination, 
                          total: (old.pagination.total || 0) + 1 
                        } : old.pagination
                      };
                    }
                  }
                  return old;
                });
              });
            }
            
            // Also update detail cache
            queryClient.setQueryData(leadKeys.detail(eventCompany, newLead._id), newLead);
          }
          
          // PHASE 3: Frontend fixes - Optimistic cache removal for removed managers
          // CRITICAL: If this is a removal event (manager removed from lead), optimistically remove the lead
          // from ALL cached manager leads queries BEFORE refetch to prevent stale restore
          const leadIdToRemove = payload?.id || payload?.leadId;
          const isRemovalEvent = isRemoved || (isOwnerChange && ownerTo.length === 0 && ownerFrom.length > 0);
          const allManagerIds = (isOwnerChange || isRemoved)
            ? [...new Set([...ownerFrom, ...ownerTo])] // All unique manager IDs (old + new, or removed)
            : targetManagerId 
              ? [targetManagerId]
              : [];
          const removedManagerIds = isRemovalEvent && ownerFrom.length > 0 ? ownerFrom : (isRemovalEvent ? allManagerIds : []);
          
          if (isRemovalEvent && leadIdToRemove && removedManagerIds.length > 0) {
            // CRITICAL FIX: Handle new queryKey format ['leads','my',company,managerId]
            // Cancel queries for leads/my with exact key match
            for (const managerId of removedManagerIds) {
              // Cancel queries for new format: ['leads','my',company,managerId]
              const myQueryKey = leadKeys.my(eventCompany, managerId);
              queryClient.cancelQueries({ queryKey: myQueryKey });
              
              // Also cancel old format queries (backward compatibility)
              queryClient.cancelQueries({
                predicate: (query) => {
                  const key = query.queryKey;
                  if (!Array.isArray(key) || key.length < 4) return false;
                  // Handle new format: ['leads','my',company,managerId]
                  if (key[0] === 'leads' && key[1] === 'my' && key[2] === eventCompany && key[3] === managerId) {
                    return true;
                  }
                  // Handle old format: ['leads',company,'list',filtersKey]
                  if (key[0] !== 'leads' || key[1] !== eventCompany || key[2] !== 'list') return false;
                  const filtersKey = key[3];
                  let params: any = null;
                  
                  // CRITICAL: Handle both old format (object) and new format (JSON string)
                  if (typeof filtersKey === 'string') {
                    try {
                      params = JSON.parse(filtersKey);
                    } catch {
                      return false;
                    }
                  } else if (typeof filtersKey === 'object' && filtersKey !== null) {
                    params = filtersKey;
                  } else {
                    return false;
                  }
                  
                  if (!params) return false;
                  // Check scope (new format) or managerId (old format)
                  const scope = params.scope;
                  const managerIdFromParams = params.managerId || (scope && scope !== 'ALL' ? scope : null);
                  return managerIdFromParams && String(managerIdFromParams) === String(managerId);
                },
              });
            }
            
            // CRITICAL FIX: Deterministically remove leadId from queryKey ['leads','my',company,removedManagerId]
            // cancelQueries -> setQueryData(filter out) -> invalidateQueries exact key
            for (const managerId of removedManagerIds) {
              const myQueryKey = leadKeys.my(eventCompany, managerId);
              
              // Remove lead from leads/my query using exact key
              queryClient.setQueryData(
                myQueryKey,
                (old: any) => {
                  if (!old) return old;
                  
                  // Helper to remove lead from array
                  const removeFromArray = (arr: any[]): any[] => {
                    const filtered = arr.filter((item: any) => String(item?._id) !== String(leadIdToRemove));
                    // Debug logs removed
                    return filtered;
                  };
                  
                  // Handle different response formats
                  if (Array.isArray(old)) {
                    return removeFromArray(old);
                  }
                  if (old.data && Array.isArray(old.data)) {
                    const filtered = removeFromArray(old.data);
                    return {
                      ...old,
                      data: filtered,
                      pagination: old.pagination ? {
                        ...old.pagination,
                        total: Math.max(0, (old.pagination.total || 0) - 1),
                      } : old.pagination,
                    };
                  }
                  if (old.items && Array.isArray(old.items)) {
                    const filtered = removeFromArray(old.items);
                    return {
                      ...old,
                      items: filtered,
                      pagination: old.pagination ? {
                        ...old.pagination,
                        total: Math.max(0, (old.pagination.total || 0) - 1),
                      } : old.pagination,
                    };
                  }
                  if (old.leads && Array.isArray(old.leads)) {
                    const filtered = removeFromArray(old.leads);
                    return {
                      ...old,
                      leads: filtered,
                      pagination: old.pagination ? {
                        ...old.pagination,
                        total: Math.max(0, (old.pagination.total || 0) - 1),
                      } : old.pagination,
                    };
                  }
                  return old;
                }
              );
              
              // Invalidate exact key to ensure fresh data on next fetch
              queryClient.invalidateQueries({ queryKey: myQueryKey });
              
              // Also handle old format queries (backward compatibility)
              queryClient.setQueriesData(
                {
                  predicate: (query) => {
                    const key = query.queryKey;
                    if (!Array.isArray(key) || key.length < 4) return false;
                    // Skip new format (already handled above)
                    if (key[0] === 'leads' && key[1] === 'my' && key[2] === eventCompany && key[3] === managerId) {
                      return false; // Already handled above
                    }
                    // Handle old format: ['leads',company,'list',filtersKey]
                    if (key[0] !== 'leads' || key[1] !== eventCompany || key[2] !== 'list') return false;
                    const filtersKey = key[3];
                    let params: any = null;
                    
                    // CRITICAL: Handle both old format (object) and new format (JSON string)
                    if (typeof filtersKey === 'string') {
                      try {
                        params = JSON.parse(filtersKey);
                      } catch {
                        return false;
                      }
                    } else if (typeof filtersKey === 'object' && filtersKey !== null) {
                      params = filtersKey;
                    } else {
                      return false;
                    }
                    
                    if (!params) return false;
                    // Check scope (new format) or managerId (old format)
                    const scope = params.scope;
                    const managerIdFromParams = params.managerId || (scope && scope !== 'ALL' ? scope : null);
                    return managerIdFromParams && String(managerIdFromParams) === String(managerId);
                  },
                },
                (old: any) => {
                  if (!old) return old;
                  
                  // Helper to remove lead from array
                  const removeFromArray = (arr: any[]): any[] => {
                    return arr.filter((item: any) => String(item?._id) !== String(leadIdToRemove));
                  };
                  
                  // Handle different response formats
                  if (Array.isArray(old)) {
                    return removeFromArray(old);
                  }
                  if (old.data && Array.isArray(old.data)) {
                    return { ...old, data: removeFromArray(old.data) };
                  }
                  if (old.items && Array.isArray(old.items)) {
                    return { ...old, items: removeFromArray(old.items) };
                  }
                  if (old.leads && Array.isArray(old.leads)) {
                    return { ...old, leads: removeFromArray(old.leads) };
                  }
                  return old;
                }
              );
            }
          }
          
          // CRITICAL: NO REFETCH - Cache is already updated via PHASE 1 setQueriesData above
          // This ensures instant synchronization between admin /leads and manager /leads/my
          // Without refetch, there's no lag - the lead disappears/appears instantly based on cache update
          
          // Remove from in-flight map immediately (no refetch to wait for)
          inFlightRefetches.delete(dedupKey);
          
          break;
        }

        case 'tasks':
        case 'tasks:changed': {
          // DETERMINISTIC REFETCH BURST: Use per-company lock with TTL to prevent duplicate bursts
          const burstLockKey = `tasks|${eventCompany}`;
          const now = Date.now();
          const lockState = burstLocks.get(burstLockKey);
          
          if (lockState && typeof lockState === 'object' && lockState.inFlight) {
            // Mark pending and return (will trigger after current burst completes)
            lockState.pending = true;
            lockState.lastEventAt = now;
            return; // Skip refetch, but event was queued
          }
          
          // Set lock state
          burstLocks.set(burstLockKey, { inFlight: true, pending: false, lastEventAt: now });
          
          // Invalidate all tasks queries for this company
          const allQueryKey = taskKeys.all(eventCompany);
          queryClient.invalidateQueries({ queryKey: allQueryKey });
          
          // REFETCH BURST: Use predicate to refetch ALL tasks queries for this company
          // This includes:
          // - Admin tasks: ['tasks', company, 'list', params] → company at index 1
          // - Manager tasks: ['tasks', company, 'list', params] where params.managerId exists → company at index 1
          // - Count queries: ['tasks', company, 'count'] → company at index 1
          // This ensures both admin AND manager views update
          const predicate = (query: any) => {
            const key = query.queryKey;
            if (!Array.isArray(key) || key.length < 2) return false;
            
            // Must be a tasks query for this company
            // Tasks queries: ['tasks', company, ...] → company at index 1
            if (key[0] === 'tasks' && key[1] === eventCompany) {
              return true;
            }
            
            return false;
          };
          
          // Count matching queries for debug
          const matchingQueries = queryClient.getQueryCache().findAll({ predicate });
          const matchedCount = matchingQueries.length;
          
          // Separate by type for detailed logging
          const listQueries = matchingQueries.filter(q => {
            const key = q.queryKey;
            return Array.isArray(key) && key.length >= 3 && key[2] === 'list';
          });
          const countQueries = matchingQueries.filter(q => {
            const key = q.queryKey;
            return Array.isArray(key) && key.length >= 3 && key[2] === 'count';
          });
          
          // Check if manager tasks query is matched (the one used by /manager/tasks page)
          const hasManagerTasksQuery = listQueries.some(q => {
            const key = q.queryKey;
            // Check if params include managerId (this is the manager /manager/tasks query)
            if (key.length >= 4) {
              const params = key[3];
              return params && typeof params === 'object' && 'managerId' in params && (params as any).managerId;
            }
            return false;
          });
          
          // PHASE 1 D: Log matched queries with full details - debug logs removed
          
          // PHASE 1 D: Refetch all matching queries (includes admin tasks, manager tasks, count queries)
          // Use type: 'active' to force refetch even if data is fresh
          if (matchedCount > 0) {
            queryClient.refetchQueries({ 
              predicate,
              type: 'active', // CRITICAL: Force refetch active queries even if data is fresh
            })
              .then(() => {
                // Remove lock after successful refetch
                const lockState = burstLocks.get(burstLockKey);
                if (lockState && typeof lockState === 'object') {
                  lockState.inFlight = false;
                  const hadPending = lockState.pending;
                  lockState.pending = false;
                  
                  // If there was a pending event, trigger one more burst
                  if (hadPending) {
                    // Trigger pending burst by calling refetch again
                    queryClient.refetchQueries({ predicate, type: 'active' })
                      .then(() => {
                        burstLocks.delete(burstLockKey);
                      })
                      .catch((err) => {
                        burstLocks.delete(burstLockKey);
                      });
                  } else {
                    burstLocks.delete(burstLockKey);
                  }
                } else {
                  burstLocks.delete(burstLockKey);
                }
              })
              .catch((err) => {
                // Remove lock even on error
                burstLocks.delete(burstLockKey);
              });
          } else {
            // Remove lock even if no queries matched
            burstLocks.delete(burstLockKey);
          }
          
          break;
        }

        case 'agents':
        case 'agents:changed': {
          // Invalidate all agents queries for this company (including count queries)
          const queryKey = agentKeys.all(eventCompany);
          queryClient.invalidateQueries({ queryKey });
          
          // Force refetch for immediate UI update (guard against loops)
          const predicate = (query: any) => {
            const key = query.queryKey;
            if (!Array.isArray(key) || key.length < 2) return false;
            return key[0] === 'agents' && key[1] === eventCompany;
          };
          
          const matchingQueries = queryClient.getQueryCache().findAll({ predicate });
          const matchedCount = matchingQueries.length;
          
          if (matchedCount > 0) {
            queryClient.refetchQueries({ predicate, type: 'active' })
              .then(() => {
                // Refetch completed
              })
              .catch((err) => {
                // Refetch failed
              });
          }
          
          break;
        }

        case 'orders':
        case 'orders:changed': {
          // CRITICAL: For managers, only invalidate if the order's client is linked to them
          // This ensures managers don't see orders for clients they're not linked to
          const isManager = client?.role === 'manager';
          const orderClientId = payload?.clientId;
          const linkedManagerIds: (string | number)[] = Array.isArray(payload?.linkedManagerIds) ? payload.linkedManagerIds : [];
          const currentManagerId = client?._id ? String(client._id) : null;
          
          if (isManager && currentManagerId) {
            // Manager viewing orders - check if they should see this order
            // If linkedManagerIds is empty or null, we should still invalidate (might be a new order)
            // Only skip if linkedManagerIds is explicitly provided and doesn't include this manager
            const shouldSeeOrder = !linkedManagerIds || linkedManagerIds.length === 0 || 
              linkedManagerIds.some((mid: string | number) => String(mid) === currentManagerId);
            
            if (shouldSeeOrder) {
              // Manager is linked to this client (or linkedManagerIds not provided) - invalidate their queries
              // The backend will filter correctly, so it's safe to invalidate
              const managerQueryKey = orderKeys.list(eventCompany, { managerId: currentManagerId });
              queryClient.invalidateQueries({ 
                queryKey: managerQueryKey,
                exact: false // Invalidate all queries that match this pattern
              });
              
              // Force refetch for immediate UI update (guard against loops)
              const refetchKey = `orders:${eventCompany}:manager:${currentManagerId}`;
              if (!refetching.has(refetchKey)) {
                refetching.add(refetchKey);
                queryClient.refetchQueries({ 
                  queryKey: managerQueryKey,
                  exact: false
                })
                  .then(() => {
                    refetching.delete(refetchKey);
                  })
                  .catch((err) => {
                    refetching.delete(refetchKey);
                  });
              }
            } else {
              // Manager is NOT linked to this client - DO NOT invalidate
              // This prevents the order from appearing in their view
              // The backend filtering will also prevent it, but this avoids unnecessary refetch
            }
          } else {
            // Admin or no clientId - invalidate all orders queries
            const queryKey = orderKeys.all(eventCompany);
            queryClient.invalidateQueries({ queryKey });
            
            // Force refetch for immediate UI update (guard against loops)
            const refetchKey = `orders:${eventCompany}`;
            if (!refetching.has(refetchKey)) {
              refetching.add(refetchKey);
              queryClient.refetchQueries({ queryKey })
                .then(() => {
                  refetching.delete(refetchKey);
                })
                .catch((err) => {
                  refetching.delete(refetchKey);
                });
            }
          }
          break;
        }

        case 'notifications':
        case 'notifications:new':
          // Invalidate notifications list and count
          queryClient.invalidateQueries({ queryKey: notificationKeys.all(eventCompany) });
          // Also update count directly if provided
          if (payload?.count !== undefined) {
            queryClient.setQueryData(notificationKeys.count(eventCompany), { count: payload.count });
          }
          break;

        case 'slots':
        case 'slots:changed':
        case 'appointments':
        case 'appointments:changed':
          // Invalidate appointments/slots queries
          queryClient.invalidateQueries({ queryKey: appointmentKeys.all(eventCompany) });
          break;

        case 'calendar':
        case 'calendar:changed':
          // Invalidate calendar events
          queryClient.invalidateQueries({ queryKey: calendarKeys.all(eventCompany) });
          break;

        case 'permissions':
        case 'permissions:updated':
          // Invalidate auth/me and company features (permissions affect both)
          queryClient.invalidateQueries({ queryKey: authKeys.me(eventCompany) });
          queryClient.invalidateQueries({ queryKey: companyKeys.features(eventCompany) });
          break;

        case 'company':
        case 'company:updated':
        case 'plan:changed':
        case 'company-plan':
        case 'company-plan-updated':
        case 'company-features':
        case 'company-features-updated':
        case 'messaging-settings':
        case 'company-messaging':
          // CRITICAL: Invalidate company features, plan, settings, and client data
          // Client data (auth/me) includes plan info, so it must be invalidated too
          console.log('[useRealtimeInvalidation] Company plan/features/messaging changed, invalidating queries:', eventType, eventCompany, payload);
          
          // Invalidate all company-related queries
          queryClient.invalidateQueries({ queryKey: companyKeys.features(eventCompany, 'tenant') });
          queryClient.invalidateQueries({ queryKey: companyKeys.settings(eventCompany, 'tenant') });
          queryClient.invalidateQueries({ queryKey: companyKeys.plan(eventCompany, 'tenant') });
          queryClient.invalidateQueries({ queryKey: authKeys.me(eventCompany, 'tenant') }); // Client data includes plan
          
          // CRITICAL: Invalidate messaging settings query key used by company pages
          // This ensures SMS/WhatsApp settings updates are reflected immediately
          // Always invalidate when company features/plan/messaging are updated since messaging settings are part of company data
          queryClient.invalidateQueries({ 
            queryKey: ['company', eventCompany, 'messaging-settings']
          });
          
          // Also invalidate home queries that depend on plan/badge
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              const key = query.queryKey;
              return Array.isArray(key) && 
                     (key[0] === 'home' || key[0] === 'bootstrap') && 
                     key[1] === eventCompany;
            }
          });
          
          // Dispatch window event to notify CompanyFeaturesContext and other components
          window.dispatchEvent(new CustomEvent('company-plan-changed', { 
            detail: { 
              plan: payload?.plan,
              oldPlan: payload?.oldPlan,
            } 
          }));
          break;

        case 'reminders':
        case 'reminders:changed':
          // Invalidate reminders (if we add a reminders hook)
          // queryClient.invalidateQueries({ queryKey: reminderKeys.all(eventCompany) });
          break;

        case 'workers':
        case 'workers:changed': {
          // Manager shifts & admin workers (schedule, attendance, reports)
          queryClient.invalidateQueries({ queryKey: ['workers'] });
          break;
        }

        case 'inventory':
        case 'inventory:changed': {
          // Invalidate all inventory queries for this company
          const queryKey = inventoryKeys.all(eventCompany);
          queryClient.invalidateQueries({ queryKey });
          
          // Force refetch for immediate UI update (guard against loops)
          const refetchKey = `inventory:${eventCompany}`;
          if (!refetching.has(refetchKey)) {
            refetching.add(refetchKey);
            queryClient.refetchQueries({ queryKey })
              .then(() => {
                refetching.delete(refetchKey);
              })
              .catch((err) => {
                refetching.delete(refetchKey);
              });
          }
          break;
        }

        case 'contracts':
        case 'contracts:changed': {
          // Invalidate all contracts queries for this company
          const queryKey = contractKeys.all(eventCompany);
          queryClient.invalidateQueries({ queryKey });
          
          // Force refetch for immediate UI update (guard against loops)
          const refetchKey = `contracts:${eventCompany}`;
          if (!refetching.has(refetchKey)) {
            refetching.add(refetchKey);
            queryClient.refetchQueries({ queryKey })
              .then(() => {
                refetching.delete(refetchKey);
              })
              .catch((err) => {
                refetching.delete(refetchKey);
              });
          }
          break;
        }

        case 'payments':
        case 'payments:changed': {
          // Invalidate all payments queries for this company
          // Note: Payments are also part of clients, so we invalidate both
          const paymentsQueryKey = ['payments', eventCompany];
          const clientsQueryKey = clientKeys.all(eventCompany);
          
          queryClient.invalidateQueries({ queryKey: paymentsQueryKey });
          queryClient.invalidateQueries({ queryKey: clientsQueryKey });
          
          // Force refetch for immediate UI update (guard against loops)
          const refetchKey = `payments:${eventCompany}`;
          if (!refetching.has(refetchKey)) {
            refetching.add(refetchKey);
            Promise.all([
              queryClient.refetchQueries({ queryKey: paymentsQueryKey }),
              queryClient.refetchQueries({ queryKey: clientsQueryKey }),
            ])
              .then(() => {
                refetching.delete(refetchKey);
              })
              .catch((err) => {
                refetching.delete(refetchKey);
              });
          }
          break;
        }

        default:
          // Generic invalidate - invalidate all queries for this company
          // This is a fallback for unknown event types
          if (eventType) {
            console.debug('[useRealtimeInvalidation] Unknown event type:', eventType, 'Invalidating all queries for company');
            queryClient.invalidateQueries({ queryKey: [eventType, eventCompany] });
          }
          break;
      }
    });

    return () => {
      off();
      // Cleanup burst locks on unmount
      burstLocks.clear();
    };
  }, [queryClient, company, client?.company]);
}